import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isRouteWaiting, subscribeRouteWait } from './routeLoading';

// A slim animated progress bar pinned to the very top of the screen that runs
// on EVERY route / menu change. The Suspense skeleton only shows while a lazy
// chunk downloads (first visit) — a cached page switches instantly with no
// feedback, so the user couldn't tell the page had changed. This bar always
// gives that "something happened" cue.
//
// It used to be a fixed animation: full in half a second, faded by 820ms,
// whatever the page was doing. On a slow link a chunk takes several seconds, so
// the bar was long gone while the screen still had not arrived and the app read
// as hung. Now it only completes when the screen is actually there: while a
// placeholder is still up (routeLoading) the bar creeps towards 90% and waits.
export default function TopRouteProgress() {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timers = useRef([]);
  const creep = useRef(null);
  const settled = useRef(true);

  const clearAll = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    if (creep.current) {
      clearInterval(creep.current);
      creep.current = null;
    }
  };

  // Fill the bar and take it away. Used both when the screen was already
  // cached (nothing to wait for) and when the wait finally ends.
  const finish = () => {
    clearAll();
    setProgress(100);
    timers.current.push(setTimeout(() => setVisible(false), 300));
    timers.current.push(setTimeout(() => setProgress(0), 560));
  };

  useEffect(() => {
    // Clear any run still in flight so rapid clicks restart cleanly.
    clearAll();
    settled.current = false;

    setVisible(true);
    setProgress(12);
    timers.current.push(setTimeout(() => setProgress(62), 80));

    // After the opening sprint, decide: if nothing is waiting the screen is
    // here, so finish. If it is, creep towards 90% for as long as it takes —
    // a bar that is still moving is the difference between "loading" and
    // "broken".
    timers.current.push(
      setTimeout(() => {
        if (!isRouteWaiting()) {
          settled.current = true;
          finish();
          return;
        }
        setProgress(78);
        creep.current = setInterval(() => {
          // Decreasing steps, so it never reaches the end on its own.
          setProgress((p) => (p >= 90 ? p : p + Math.max(0.4, (90 - p) / 12)));
        }, 300);
      }, 320),
    );

    return clearAll;
  }, [location.pathname]);

  // The screen landed (or a second one did) — close the bar.
  useEffect(() => {
    return subscribeRouteWait((waiting) => {
      if (!waiting && !settled.current) {
        settled.current = true;
        finish();
      }
    });
  }, []);

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: 3,
        zIndex: 2000,
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transition: 'opacity .25s ease',
      }}>
      <div
        style={{
          height: '100%',
          width: `${progress}%`,
          background: 'linear-gradient(90deg,#1A56DB,#3B82F6,#22D3EE)',
          boxShadow: '0 0 8px rgba(59,130,246,.7)',
          borderRadius: '0 2px 2px 0',
          transition: 'width .3s ease',
        }}
      />
    </div>
  );
}
